/**
 * Notification center — the panel opened by the bell. A modal dialog listing
 * the signed-in user's recent notifications: unread highlighted, click-through
 * to the linked contact or deal, mark-one / mark-all read.
 *
 * Modal (not an anchored popover) on purpose: it opens identically from the
 * desktop sidebar and any future mobile placement, with no positioning math.
 */

import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell,
  CheckCheck,
  CheckSquare,
  CreditCard,
  Flame,
  GitBranch,
  Inbox,
  Mail,
  TrendingUp,
  UserPlus,
  type LucideIcon,
} from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
} from "./api";
import type { RelayNotification } from "./types";

/* ------------------------------------------------------------------ */
/*  Presentation helpers                                               */
/* ------------------------------------------------------------------ */

const EVENT_META: Record<string, { icon: LucideIcon; label: string }> = {
  hot_lead: { icon: Flame, label: "Hot lead" },
  new_contact: { icon: UserPlus, label: "New contact" },
  contact_updated: { icon: UserPlus, label: "Contact updated" },
  deal_created: { icon: CreditCard, label: "New deal" },
  deal_stage_changed: { icon: TrendingUp, label: "Deal moved" },
  deal_value_changed: { icon: TrendingUp, label: "Deal value" },
  email_reply: { icon: Mail, label: "Email reply" },
  sequence_completed: { icon: GitBranch, label: "Sequence complete" },
  task_due: { icon: CheckSquare, label: "Task due" },
};

function eventMeta(eventType: string): { icon: LucideIcon; label: string } {
  return EVENT_META[eventType] ?? { icon: Bell, label: "Notification" };
}

/** Icon-chip tint by priority — urgent reads loudest, low recedes. */
function priorityChipClasses(priority: string): string {
  switch (priority) {
    case "urgent":
      return "bg-destructive/15 text-destructive";
    case "high":
      return "bg-warning/15 text-warning";
    case "low":
      return "bg-muted text-muted-foreground";
    default:
      return "bg-primary/10 text-primary";
  }
}

function formatRelativeTime(iso: string): string {
  const elapsedMs = Date.now() - new Date(iso).getTime();
  const sec = Math.round(elapsedMs / 1000);
  if (sec < 45) return "just now";
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 7) return `${day}d ago`;
  return new Date(iso).toLocaleDateString();
}

/** Deep link for a notification, or null when there's nothing to open. */
function notificationHref(n: RelayNotification): string | null {
  if (n.deal_id) return `/deals/${n.deal_id}`;
  if (n.contact_id) return `/contacts/${n.contact_id}`;
  return null;
}

/* ------------------------------------------------------------------ */
/*  Row                                                                */
/* ------------------------------------------------------------------ */

function NotificationRow({
  notification,
  onActivate,
}: {
  notification: RelayNotification;
  onActivate: (n: RelayNotification) => void;
}) {
  const { icon: Icon } = eventMeta(notification.event_type);
  const unread = !notification.read_at;

  return (
    <button
      type="button"
      onClick={() => onActivate(notification)}
      className={cn(
        "flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-accent/60",
        unread && "bg-primary/[0.06]",
      )}
    >
      <span
        className={cn(
          "mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg",
          priorityChipClasses(notification.priority),
        )}
      >
        <Icon className="h-4 w-4" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2">
          <span
            className={cn(
              "truncate text-sm",
              unread ? "font-semibold text-foreground" : "font-medium text-foreground/90",
            )}
          >
            {notification.title}
          </span>
          {unread && (
            <span
              className="h-2 w-2 shrink-0 rounded-full bg-primary"
              aria-label="Unread"
            />
          )}
        </span>
        <span className="mt-0.5 line-clamp-2 block text-xs text-muted-foreground">
          {notification.message}
        </span>
        <span className="mt-1 block text-[11px] text-muted-foreground/70">
          {formatRelativeTime(notification.created_at)}
        </span>
      </span>
    </button>
  );
}

/* ------------------------------------------------------------------ */
/*  Center                                                             */
/* ------------------------------------------------------------------ */

export function NotificationCenter({
  open,
  onOpenChange,
  confirmNavigate = () => true,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Gate a linked notification's activation (mark-read + close + navigate)
   * behind the app-wide unsaved-changes guard. Defaults to a no-op that
   * always allows activating, so this component works standalone outside
   * AppShell.
   */
  confirmNavigate?: () => boolean;
}) {
  const navigate = useNavigate();
  const { data: notifications, isLoading } = useNotifications();
  const markRead = useMarkNotificationRead();
  const markAll = useMarkAllNotificationsRead();

  const unreadCount = useMemo(
    () => (notifications ?? []).filter((n) => !n.read_at).length,
    [notifications],
  );

  const handleActivate = (n: RelayNotification) => {
    const href = notificationHref(n);
    // Linked notifications mark-read, close, and route away together — gate
    // all three behind the guard so cancelling leaves the notification
    // unread and the center open, not just the route change.
    if (href) {
      if (!confirmNavigate()) return;
      if (!n.read_at) markRead.mutate(n.id);
      onOpenChange(false);
      navigate(href);
      return;
    }
    // No link to follow — marking read has no navigation side effect.
    if (!n.read_at) markRead.mutate(n.id);
  };

  const items = notifications ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="gap-0 overflow-hidden p-0 sm:max-w-md">
        <DialogHeader className="flex-row items-center justify-between space-y-0 border-b px-4 py-3">
          <DialogTitle className="text-base">
            Notifications
            {unreadCount > 0 && (
              <span className="ml-2 text-sm font-normal text-muted-foreground">
                {unreadCount} unread
              </span>
            )}
          </DialogTitle>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 gap-1.5 text-xs"
              onClick={() => markAll.mutate()}
              disabled={markAll.isPending}
            >
              <CheckCheck className="h-3.5 w-3.5" />
              Mark all read
            </Button>
          )}
        </DialogHeader>

        <div className="max-h-[60vh] overflow-y-auto">
          {isLoading ? (
            <div className="space-y-3 p-4">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="flex items-start gap-3">
                  <Skeleton className="h-8 w-8 shrink-0 rounded-lg" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-3.5 w-2/3" />
                    <Skeleton className="h-3 w-full" />
                  </div>
                </div>
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 px-6 py-14 text-center">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                <Inbox className="h-6 w-6 text-muted-foreground" />
              </span>
              <p className="text-sm font-medium text-foreground">
                You&apos;re all caught up
              </p>
              <p className="text-xs text-muted-foreground">
                Hot leads, new deals, and stage changes will show up here.
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {items.map((n) => (
                <li key={n.id}>
                  <NotificationRow notification={n} onActivate={handleActivate} />
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
